/* Benachrichtigungen und Web Push.
 *
 * Ablauf: Eine Aktion in der App schreibt eine Zeile in `notifications`.
 * Ein Webhook auf dieser Tabelle verschickt daraus den eigentlichen Push an
 * die in `push_subscriptions` hinterlegten Geräte. Die App selbst verschickt
 * nichts — sie trägt nur ein.
 */

import { supabase, VAPID_PUBLIC_KEY } from './client.js';
import { fullName } from '../domain/format.js';

const MAX_ITEMS = 30;

export async function loadNotifications() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(MAX_ITEMS);

  if (error) {
    console.error('[notifications] Laden fehlgeschlagen:', error.message);
    return [];
  }
  return data || [];
}

/**
 * Live-Aktualisierung. Setzt voraus, dass für die Tabelle in Supabase
 * "Realtime replication" eingeschaltet ist — ist sie es nicht, passiert
 * schlicht nichts und die Liste aktualisiert sich beim nächsten Laden.
 */
export function subscribeNotifications(onInsert) {
  if (!supabase || !supabase.channel) return null;
  try {
    return supabase
      .channel('notif-stream')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload) => onInsert(payload.new)
      )
      .subscribe();
  } catch {
    return null;
  }
}

export function unsubscribeNotifications(channel) {
  if (channel && supabase) {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* egal — die Verbindung endet ohnehin mit der Seite */
    }
  }
}

/**
 * Ereignis eintragen; der Versand passiert serverseitig per Webhook.
 *
 * Für die Typen aus domain/notifications.js wird KEIN fertiger Text
 * gespeichert: `title` ist der Spielername, `body` bleibt leer. Den Text
 * "News: <Thema>" bauen Glocke und Push in der Sprache des Empfängers aus dem
 * Typ. `title`/`body` lassen sich weiterhin mitgeben (Rückfall, alte Typen).
 *
 * audience ist immer 'all': Sie steuert nur, wer eintragen DARF (Policy
 * notifications_insert). Wer Spieler speichern kann (admin, board, data), darf
 * 'all' eintragen; mit 'board' würde ein data-Konto stillschweigend abgewiesen.
 * Wer die Meldung SIEHT, regelt notifications_select unabhängig davon.
 */
export async function emitNotification({ type, player, title, body, createdBy }) {
  if (!supabase) return;
  const name = player ? fullName(player) : null;
  try {
    const { error } = await supabase.from('notifications').insert({
      type,
      player_id: player ? String(player.id) : null,
      player_name: name,
      title: title ?? name ?? '',
      body: body ?? null,
      created_by: createdBy || '',
      audience: 'all',
    });
    if (error) console.error('[notifications] Eintragen fehlgeschlagen:', error.message);
  } catch (err) {
    console.error('[notifications] Eintragen fehlgeschlagen:', err && err.message);
  }
}

/**
 * Meldungen zu einem gelöschten Spieler entfernen (notifications.player_id ist Text ohne
 * Fremdschlüssel — die Datenbank löscht sie nicht mit). Nur admin darf (Regel notifications_delete_admin).
 * Löst keinen Push aus (der Webhook hängt nur an INSERT). Wirft bei Fehler.
 */
export async function deleteNotificationsForPlayer(playerId) {
  if (!supabase) throw new Error('notConnected');
  const { error } = await supabase.from('notifications').delete().eq('player_id', String(playerId));
  if (error) {
    console.error('[notifications] Löschen fehlgeschlagen:', error.message);
    throw new Error('deleteFailed');
  }
}

// ---------- Web Push ----------

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    !!VAPID_PUBLIC_KEY
  );
}

async function getRegistration() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export async function isPushSubscribed() {
  const reg = await getRegistration();
  if (!reg) return false;
  try {
    return !!(await reg.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/**
 * Gerät für Push anmelden.
 * Liefert { ok: true } oder { ok: false, reason: <i18n-Schlüssel> } — die
 * Meldung formuliert die Oberfläche, damit sie übersetzt werden kann.
 */
export async function enablePush(userId) {
  if (!pushSupported()) return { ok: false, reason: 'pushUnsupported' };
  if (!supabase || !userId) return { ok: false, reason: 'loginFirst' };

  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'pushDenied' };

  const reg = await getRegistration();
  if (!reg) return { ok: false, reason: 'swNotReady' };

  try {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    const json = sub.toJSON();
    const { error } = await supabase.from('push_subscriptions').upsert(
      {
        user_id: userId,
        endpoint: sub.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        user_agent: navigator.userAgent,
      },
      { onConflict: 'endpoint' }
    );
    if (error) {
      console.error('[push] Abo speichern fehlgeschlagen:', error.message);
      return { ok: false, reason: 'subscribeFailed' };
    }
    return { ok: true };
  } catch (err) {
    console.error('[push] Anmeldung fehlgeschlagen:', err);
    return { ok: false, reason: 'subscribeFailed' };
  }
}
